/**
 * 세부 탭 — 주메뉴 안에서 화면을 바꾸는 단추 **한 벌** (v2.252.0 · 화면 통일 ①).
 *
 * 왜: 10-14 까지 화면마다 탭을 따로 만들어 3계통 12가지 모양(파란 채움 알약·밑줄·상자/알약/붙은 상자)이
 *     섞여 있었다(사용자 「주메뉴마다 세부메뉴 버튼이 제각기 생기고 폰트·크기도 달라 지저분하다」).
 *     vCenter(Clarity) 식 **밑줄 탭** 하나로 — 14px · 높이 36px · 켜진 탭 = 파란 글자 + 2px 파란 밑줄.
 *
 * 규칙
 *   · 「화면을 바꾸는 것」만 이것으로. 같은 목록을 **걸러 보는 것**(진행 중/전체 · 상태)은 `SegmentedControl`.
 *   · 모양은 index.css 의 `.ui-tab*` 한 곳에만 — 화면 파일에 탭 단추 class 를 다시 적지 말 것(`check:ui` 가 잡는다).
 *   · 접근성: role=tablist/tab · aria-selected · ←→ Home End 로 옮겨 다니기. 그물(verify:sales-tab 등)이 이 속성으로 탭을 찾는다.
 *   · `count` = 회색 작은 건수(안내) · `badge` = 빨간 배지(처리할 것이 있을 때 · 0 이면 안 그림).
 *   · `group` = 묶음 이름(거래 문서함 「만드는 문서 / 받은 문서」) — 앞 탭과 다르면 작은 이름을 앞에 그린다.
 *   · `right` = 탭 줄 오른쪽 끝(연도 고르기 · 엑셀 · 안내 글). 좁으면 아래 줄로 내려간다.
 */

import { Fragment, useRef, type KeyboardEvent, type ReactNode } from 'react';

export type PageTabItem<K extends string> = {
  key: K;
  label: ReactNode;
  /** 회색 작은 숫자 — 건수 안내 */
  count?: number;
  /** 빨간 배지 — 처리할 것이 있을 때(0 이면 안 그림) */
  badge?: number;
  /** 마우스를 올리면 보이는 설명 */
  title?: string;
  /** 묶음 이름 — 앞 탭과 다르면 구분선 + 작은 이름을 앞에 그린다 */
  group?: string;
  /** 권한 등으로 안 보이는 탭 */
  hidden?: boolean;
};

export function PageTabs<K extends string>({
  tabs,
  value,
  onChange,
  ariaLabel,
  right,
  className,
}: {
  tabs: readonly PageTabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  /** 그물·보조기기가 이 탭 묶음을 부르는 이름 (예: 「영업진행 보기」) */
  ariaLabel: string;
  /** 오른쪽 끝에 두는 것 — 연도 고르기·엑셀 단추·안내 글 */
  right?: ReactNode;
  /** 바깥 여백을 바꿀 때 (기본 mb-4) */
  className?: string;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const visible = tabs.filter((t) => !t.hidden);

  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number): void => {
    const n = visible.length;
    let j: number;
    if (e.key === 'ArrowRight') j = (i + 1) % n;
    else if (e.key === 'ArrowLeft') j = (i - 1 + n) % n;
    else if (e.key === 'Home') j = 0;
    else if (e.key === 'End') j = n - 1;
    else return;
    e.preventDefault();
    onChange(visible[j]!.key);
    listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[j]?.focus();
  };

  let prevGroup: string | undefined;
  const hasRight = right !== undefined && right !== null && right !== false;

  return (
    <div className={`ui-tabs ${className ?? ''}`}>
      <div ref={listRef} role="tablist" aria-label={ariaLabel} className="ui-tabs-list">
        {visible.map((t, i) => {
          const on = t.key === value;
          const groupHead = t.group !== undefined && t.group !== prevGroup;
          prevGroup = t.group;
          return (
            <Fragment key={t.key}>
              {groupHead && (
                <span className={`ui-tab-group${i > 0 ? ' ui-tab-group-next' : ''}`} aria-hidden="true">
                  {t.group}
                </span>
              )}
              <button
                type="button"
                role="tab"
                aria-selected={on}
                tabIndex={on ? 0 : -1}
                title={t.title}
                onClick={() => onChange(t.key)}
                onKeyDown={(e) => onKey(e, i)}
                className="ui-tab"
              >
                <span className="ui-tab-label">{t.label}</span>
                {t.count !== undefined && <span className="ui-tab-count">{t.count}</span>}
                {(t.badge ?? 0) > 0 && (
                  <span className="ui-tab-badge">{t.badge! > 99 ? '99+' : t.badge}</span>
                )}
              </button>
            </Fragment>
          );
        })}
      </div>
      {hasRight && <div className="ui-tabs-right">{right}</div>}
    </div>
  );
}
